import React, { useState, useEffect } from "react";
import { Button, Input, Card, Tabs, Row, Col, Space, Tag, Tooltip, Modal, List } from "antd";
import {
  DeleteOutlined,
  HistoryOutlined,
  MoreOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import useApiCall from "../common/api-call";
import PlanGraphViz from "./PlanGraphViz";

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`thread-tabpanel-${index}`}
      aria-labelledby={`thread-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: 24 }}>{children}</div>}
    </div>
  );
};

const HISTORY_STORAGE_KEY = "debug_automation_history";

const Debug = () => {
  const [name, setName] = useState("");
  const [tenant, setTenant] = useState("");
  const [scope, setScope] = useState("");
  const [automation, setAutomation] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const { apiCall } = useApiCall();

  // Load history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
      } catch (e) {
        console.error("Error parsing history from localStorage:", e);
        setHistory([]);
      }
    }
  }, []);

  const handleTabChange = (key) => {
    setActiveTab(parseInt(key));
  };

  const saveToHistory = () => {
    // Create a new history entry
    const newEntry = {
      id: Date.now().toString(), // Use timestamp as unique ID
      name: name || `${tenant}-${scope}-${automation}`, // Use name if provided, otherwise create a default name
      tenant,
      scope,
      automation,
      timestamp: new Date().toISOString(),
    };

    // Check if this combination already exists to avoid duplicates
    const entryExists = history.some(
      item =>
        item.tenant === tenant &&
        item.scope === scope &&
        item.automation === automation,
    );

    if (!entryExists) {
      // Add to history, limiting to most recent 10 entries
      const updatedHistory = [newEntry, ...history].slice(0, 10);
      setHistory(updatedHistory);

      // Save to localStorage
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
    }
  };

  const loadFromHistory = historyItem => {
    setName(historyItem.name || "");
    setTenant(historyItem.tenant);
    setScope(historyItem.scope);
    setAutomation(historyItem.automation);
    setHistoryDialogOpen(false);
  };

  const removeHistoryItem = (e, id) => {
    e.stopPropagation(); // Prevent triggering the parent click handler
    const updatedHistory = history.filter(item => item.id !== id);
    setHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  const handleDebug = async () => {
    setLoading(true);
    setResult(null);

    try {
      const endpoint = "/automation/debug";
      const requestBody = {
        tenantId: tenant,
        scopeId: scope,
        automationId: automation,
      };
      const response = await apiCall(endpoint, "POST", requestBody, "admin");

      // Save successful queries to history
      saveToHistory();

      setResult(response);
      // Reset to first tab when new data arrives
      setActiveTab(0);
    } catch (error) {
      console.error("Error debugging automation:", error);
      setResult({ error: "Failed to debug automation" });
    } finally {
      setLoading(false);
    }
  };

  // Show GraphViz visualization
  const showGraphVisualization = () => {
    setGraphDialogOpen(true);
  };

  // Generate DOT data from the current thread's plan
  const generateDotData = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      if (currentThread && currentThread.plan) {
        showGraphVisualization();
      }
    }
  };

  // Format relative time for history items
  const formatRelativeTime = timestamp => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const visibleHistory = history.slice(0, 3);
  const hasMoreHistory = history.length > 3;

  const getCurrentThreadPlan = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      return currentThread.plan || "";
    }
    return "";
  };

  const getCurrentThreadMemory = () => {
    if (result && result.threads && result.threads.length > activeTab) {
      const currentThread = result.threads[activeTab];
      return currentThread.memory || "";
    }
    return "";
  };

  return (
    <div style={{ padding: 24 }}>
      {history.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            <h6 style={{ margin: 0, fontSize: "1rem", fontWeight: 500 }}>Recent Queries</h6>
          </div>
          <Space wrap>
            {visibleHistory.map(item => (
              <Tag
                key={item.id}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  removeHistoryItem(e, item.id);
                }}
                onClick={() => loadFromHistory(item)}
                style={{ cursor: "pointer", marginBottom: 8 }}
              >
                {item.name ||
                  `${item.tenant} / ${item.scope} / ${item.automation}`}
              </Tag>
            ))}
            {hasMoreHistory && (
              <Tag
                icon={<MoreOutlined />}
                onClick={() => setHistoryDialogOpen(true)}
                style={{ cursor: "pointer", marginBottom: 8 }}
              >
                More
              </Tag>
            )}
          </Space>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>Name</div>
            <Input
              placeholder="Optional identifier for this debug query"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Tenant ID</div>
            <Input
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Scope ID</div>
            <Input
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <div style={{ marginBottom: 8 }}>Automation ID</div>
            <Input
              value={automation}
              onChange={(e) => setAutomation(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              block
              onClick={handleDebug}
              disabled={!tenant || !scope || !automation || loading}
              loading={loading}
              style={{ marginTop: 24 }}
            >
              {loading ? "Processing..." : "Debug"}
            </Button>
          </Col>
        </Row>
      </Card>

      {result && result.threads && result.threads.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div
            style={{
              borderBottom: "1px solid #EAEEF0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tabs
              activeKey={activeTab.toString()}
              onChange={handleTabChange}
              style={{ flex: 1 }}
              items={result.threads.map((thread, index) => ({
                key: index.toString(),
                label: `Thread ${thread.threadId}`,
              }))}
            />
            <Space style={{ marginRight: 16 }}>
              <Tooltip title="View Graph Visualization">
                <Button
                  type="primary"
                  icon={<AppstoreOutlined />}
                  onClick={showGraphVisualization}
                  size="small"
                />
              </Tooltip>
            </Space>
          </div>

          {result.threads.map((thread, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Card>
                    <h6 style={{ marginBottom: 16, fontSize: "1rem", fontWeight: 500 }}>Plan</h6>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                        maxHeight: "400px",
                      }}
                    >
                      {thread.plan || "No plan available"}
                    </pre>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card>
                    <h6 style={{ marginBottom: 16, fontSize: "1rem", fontWeight: 500 }}>Memory</h6>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        overflow: "auto",
                        maxHeight: "400px",
                      }}
                    >
                      {thread.memory || "No memory available"}
                    </pre>
                  </Card>
                </Col>
              </Row>
            </TabPanel>
          ))}
        </Card>
      )}

      {result && (!result.threads || result.threads.length === 0) && (
        <Card>
          <h6 style={{ marginBottom: 16, fontSize: "1rem", fontWeight: 500 }}>Debug Result</h6>
          <pre style={{ overflow: "auto", maxHeight: "400px" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Card>
      )}

      {/* History Selection Modal */}
      <Modal
        open={historyDialogOpen}
        onCancel={() => setHistoryDialogOpen(false)}
        width="800px"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Debugging History
          </div>
        }
        footer={[
          <Button key="close" onClick={() => setHistoryDialogOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <List
          dataSource={history}
          renderItem={(item) => (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() => loadFromHistory(item)}
              actions={[
                <Button
                  key="delete"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeHistoryItem(e, item.id);
                  }}
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  item.name ||
                  `${item.tenant} / ${item.scope} / ${item.automation}`
                }
                description={`Tenant: ${item.tenant} | Scope: ${item.scope} | Automation: ${item.automation} | ${formatRelativeTime(
                  item.timestamp,
                )}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* GraphViz Visualization Modal */}
      <Modal
        open={graphDialogOpen}
        onCancel={() => setGraphDialogOpen(false)}
        width="100%"
        style={{ top: 0, paddingBottom: 0 }}
        bodyStyle={{ height: "calc(100vh - 55px)", padding: 0 }}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <AppstoreOutlined style={{ marginRight: 8 }} />
              Plan Visualization
            </div>
            <Button onClick={() => setGraphDialogOpen(false)}>Close</Button>
          </div>
        }
        footer={null}
      >
        <PlanGraphViz
          planData={getCurrentThreadPlan()}
          memData={getCurrentThreadMemory()}
        />
      </Modal>
    </div>
  );
};

export default Debug;
